import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAppSettings, getLiveStatus } from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { unscopedKey } from "@/lib/queryClient";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useAuthStore } from "@/stores/auth";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useAirPlay } from "@/features/playback/useAirPlay";
import { useChromecast } from "@/features/playback/useChromecast";

/**
 * M11b — public live-stream viewer at `/live`.
 *
 * Three responsibilities:
 *   1. Poll `/api/live/status` every 4 s (LL-HLS feeds come up
 *      quickly when the camera connects). Determine display state:
 *      - `enabled === false` → "Disabled" pill + admin link
 *      - `active && ready`   → mount `<HlsVideo live>` + LIVE pill
 *      - everything else     → offline placeholder + custom message
 *   2. Render the player with the LL-HLS buffer profile so a
 *      weak-uplink camera doesn't stall (`<HlsVideo live>` switches
 *      to the legacy `js/live.js` config).
 *   3. Surface signed-in admin extras (cockpit link to /admin/live),
 *      AirPlay (Safari/iOS picker via `useAirPlay`), and Chromecast
 *      (Google Cast SDK default media receiver via `useChromecast`).
 *
 * Public — no auth gate. Anonymous viewers land directly on the
 * shell. Signed-in extras gate off the auth store populated by `useMe()`.
 *
 * Replaces the M11a placeholder of the same name; the season-view
 * "Watch Live" CTA (A8) already routed here, so this is the
 * implementation of that promise.
 */
export function LiveShell() {
  const settingsQuery = useQuery({
    queryKey: unscopedKey("app-settings"),
    queryFn: getAppSettings,
    staleTime: 5 * 60_000,
  });
  const settings = settingsQuery.data?.settings;
  const settingsLiveEnabled = settings ? coerceBool(settings.live_enabled) : true;

  // Poll every 4 s when the page is visible. We gate the poll on
  // the SETTINGS flag (no point polling when an admin explicitly
  // disabled the feature in app settings — that's a long-lived
  // decision cached for 5 min) but the polled status ALSO carries
  // an `enabled` field; an admin who disables live mid-session
  // gets propagated to the viewer via the next 4-s poll, not the
  // next 5-min settings refetch. The disabled-state check below
  // honors BOTH sources. Reviewer-flagged on PR #22 P2.
  const statusQuery = useQuery({
    queryKey: unscopedKey("live-status"),
    queryFn: getLiveStatus,
    enabled: settingsLiveEnabled,
    refetchInterval: settingsLiveEnabled ? 4_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  const status = statusQuery.data;

  const authMe = useAuthStore((s) => s.me);
  const isAdmin = !!authMe?.user?.roles?.includes("admin");

  const appName = settings?.app_name || "tktic";
  useDocumentMeta({
    title: `Watch Live · ${appName}`,
    description: settings?.season_intro || "Live broadcast.",
    ogType: "video.other",
  });

  // Disabled when EITHER the 5-min cached settings flag says no
  // (steady-state admin decision) OR the 4-s live-status poll
  // reports enabled=false (runtime admin flip — viewers already on
  // /live should see Disabled immediately, not after the settings
  // cache expires). Reviewer-flagged on PR #22 P2.
  const isDisabled = !settingsLiveEnabled || status?.enabled === false;
  const isActive = !isDisabled && !!(status?.active && status?.ready);
  const offlineMessage =
    settings?.live_offline_message || "No live stream right now.";

  return (
    <main
      data-testid="live-shell"
      data-live-state={
        isDisabled ? "disabled" : isActive ? "live" : "offline"
      }
      className="min-h-screen bg-canvas text-ink"
    >
      <header
        data-testid="live-header"
        className="px-6 py-3 border-b border-rule flex items-center justify-between gap-3 max-w-7xl mx-auto"
      >
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="live-back-link"
            className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink"
          >
            ← Back to Matches
          </Link>
          <h1 className="text-sm font-display uppercase tracking-wider text-ink">
            Now Streaming
          </h1>
          <StatusPill state={isDisabled ? "disabled" : isActive ? "live" : "offline"} />
        </div>
        {isAdmin ? (
          <Link
            to="/admin/live"
            data-testid="live-admin-cockpit-link"
            className="text-[0.6rem] text-ink-muted hover:text-ink font-display uppercase tracking-wider rounded border border-rule px-2 py-1"
          >
            Open admin live cockpit →
          </Link>
        ) : null}
      </header>

      <section className="px-6 py-6 max-w-5xl mx-auto flex flex-col gap-3">
        {isDisabled ? (
          <DisabledPlaceholder />
        ) : isActive ? (
          <LivePlayer />
        ) : (
          <OfflinePlaceholder
            message={offlineMessage}
            reachable={status?.reachable !== false}
          />
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ state }: { state: "disabled" | "live" | "offline" }) {
  if (state === "live") {
    return (
      <span
        data-testid="live-status-pill"
        data-state="live"
        className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-white"
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse"
          aria-hidden="true"
        />
        LIVE
      </span>
    );
  }
  if (state === "disabled") {
    return (
      <span
        data-testid="live-status-pill"
        data-state="disabled"
        className="inline-flex items-center rounded-full border border-rule bg-bg-card px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
      >
        Disabled
      </span>
    );
  }
  return (
    <span
      data-testid="live-status-pill"
      data-state="offline"
      className="inline-flex items-center rounded-full border border-rule bg-bg-card px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
    >
      Offline
    </span>
  );
}

// ---------------------------------------------------------------------------
// Live player — HlsVideo + AirPlay
// ---------------------------------------------------------------------------

function LivePlayer() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { available: airplayAvailable, casting, prompt: openAirplay } =
    useAirPlay(videoRef);
  const chromecast = useChromecast(videoRef, () => ({
    url: "/api/live/hls/index.m3u8",
    contentType: "application/x-mpegURL",
    streamType: "LIVE",
    title: "Tktic Live Stream",
    subtitle: "Live now",
  }));

  return (
    <div data-testid="live-player" className="flex flex-col gap-2">
      <div className="relative">
        <HlsVideo
          ref={videoRef}
          hlsUrl="/api/live/hls/index.m3u8"
          // No MP4 fallback for live — pass empty string; HlsVideo's
          // useMp4Fallback short-circuits to onUnavailable.
          mp4Url=""
          live
          // Best-effort autoplay on manifest readiness — legacy
          // `js/live.js` calls `play().catch(() => {})` after both
          // native `loadedmetadata` and hls.js `MANIFEST_PARSED` so
          // a viewer landing on /live with the feed already active
          // doesn't see a paused player. Browser autoplay policy
          // may still block (no user gesture yet) — controls remain
          // so the viewer can tap play in that case.
          // Reviewer-flagged on PR #22 P2.
          autoPlay
          controls
          className="w-full aspect-video rounded bg-black"
          data-testid="live-video"
        />
      </div>
      <div
        data-testid="live-meta-row"
        className="flex items-center justify-between gap-2 text-xs font-mono text-ink-muted"
      >
        <span>Streaming from the field camera.</span>
        {airplayAvailable || chromecast.supportedBrowser ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {airplayAvailable ? (
              <button
                type="button"
                onClick={openAirplay}
                data-testid="live-airplay-btn"
                data-casting={casting ? "true" : undefined}
                className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white"
              >
                AirPlay{casting ? " · ON" : ""}
              </button>
            ) : null}
            {chromecast.supportedBrowser ? (
              <button
                type="button"
                onClick={chromecast.toggle}
                disabled={!chromecast.sdkReady}
                title={chromecast.unavailableReason || undefined}
                data-testid="live-chromecast-btn"
                data-casting={chromecast.casting ? "true" : undefined}
                className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cast{chromecast.casting ? ` · ${chromecast.deviceName || "ON"}` : ""}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Placeholders
// ---------------------------------------------------------------------------

function DisabledPlaceholder() {
  return (
    <div
      data-testid="live-disabled-placeholder"
      className="aspect-video bg-bg-card border border-rule rounded flex flex-col items-center justify-center text-ink-muted gap-2 p-6 text-center"
    >
      <p className="text-sm font-display uppercase tracking-wider">
        Live streaming is disabled
      </p>
      <p className="text-xs font-mono">
        An admin has turned off live streaming in app settings.
      </p>
    </div>
  );
}

function OfflinePlaceholder({
  message,
  reachable,
}: {
  message: string;
  reachable: boolean;
}) {
  return (
    <div
      data-testid="live-offline-placeholder"
      className="aspect-video bg-bg-card border border-rule rounded flex flex-col items-center justify-center text-ink-muted gap-2 p-6 text-center"
    >
      <p className="text-sm font-display uppercase tracking-wider">
        Stream offline
      </p>
      <p
        data-testid="live-offline-message"
        className="text-xs font-mono max-w-md"
      >
        {message}
      </p>
      {!reachable ? (
        <p
          data-testid="live-unreachable-detail"
          className="text-[0.6rem] font-mono text-destructive"
        >
          Streaming server is unreachable. Try again in a moment.
        </p>
      ) : null}
    </div>
  );
}
